import { useState } from 'react';
import { auditApi } from '../services/api/endpoints/audit';
import type { FiscalYear, AuditorCredentials, AuditValidation } from '../types/fiscal-year';
import type { 
  AuditHistoryResponse, 
  AuditListResponse, 
  AuditListFilters,
  TokenRequest 
} from '../types/audit';

export function useFiscalYearAudit() {
  const [loading, setLoading] = useState(false);
  const [auditHistory, setAuditHistory] = useState<AuditHistoryResponse | null>(null);
  const [auditList, setAuditList] = useState<AuditListResponse | null>(null);

  // Demander un token d'audit
  const requestAuditToken = async (credentials: TokenRequest): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    try {
      const response = await auditApi.requestToken(credentials);
      return {
        success: response.success ?? true,
        message: response.data?.message ?? 'Token demandé avec succès'
      };
    } catch (error) {
      console.error('Error requesting audit token:', error);
      return {
        success: false,
        message: 'Erreur lors de la demande de token'
      };
    } finally {
      setLoading(false);
    }
  };

  // Valider un token d'audit
  const validateAuditToken = async (token: string): Promise<{ valid: boolean; message: string }> => {
    setLoading(true);
    try {
      const response = await auditApi.validateToken({ token });
      return {
        valid: response.data?.valid ?? false,
        message: response.data?.message ?? 'Token validé'
      };
    } catch (error) {
      console.error('Error validating audit token:', error);
      return {
        valid: false,
        message: 'Erreur lors de la validation du token'
      };
    } finally {
      setLoading(false);
    }
  };

  // Auditer un exercice comptable (méthode legacy pour compatibilité)
  const validateAudit = async (fiscalYear: FiscalYear, credentials: AuditorCredentials): Promise<AuditValidation> => {
    setLoading(true);
    try {
      if (!credentials.token) {
        return {
          success: false,
          message: 'Token manquant',
          errors: ['Le token d\'audit est requis']
        };
      }

      // Valider d'abord le token
      const tokenValidation = await auditApi.validateToken({ token: credentials.token });
      if (!tokenValidation.data?.valid) {
        return {
          success: false,
          message: tokenValidation.data?.message ?? 'Token invalide',
          errors: ['Token invalide']
        };
      }

      // Effectuer l'audit
      const auditResponse = await auditApi.auditFiscalYear(fiscalYear.id, {
        auditorCredentials: {
          name: credentials.name,
          registrationNumber: credentials.registrationNumber,
          token: credentials.token
        }
      });

      if (auditResponse.success) {
        return {
          success: true,
          message: 'Audit validé avec succès'
        };
      } else {
        return {
          success: false,
          message: auditResponse.data?.message ?? 'Erreur lors de l\'audit'
        };
      }
    } catch (error) {
      console.error('Error validating audit:', error);
      return {
        success: false,
        message: 'Erreur lors de la validation',
        errors: ['Une erreur est survenue lors de la validation']
      };
    } finally {
      setLoading(false);
    }
  };

  // Auditer un exercice comptable (nouvelle méthode)
  const auditFiscalYear = async (
    fiscalYearId: string, 
    credentials: AuditorCredentials, 
    comments?: string
  ): Promise<AuditValidation> => {
    setLoading(true);
    try {
      if (!credentials.token) {
        return {
          success: false,
          message: 'Token manquant',
          errors: ['Le token d\'audit est requis']
        };
      }

      const response = await auditApi.auditFiscalYear(fiscalYearId, {
        auditorCredentials: {
          name: credentials.name,
          registrationNumber: credentials.registrationNumber,
          token: credentials.token
        },
        comments
      });

      return {
        success: response.success ?? false,
        message: response.data?.message ?? 'Audit effectué'
      };
    } catch (error) {
      console.error('Error auditing fiscal year:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'audit',
        errors: ['Une erreur est survenue lors de l\'audit']
      };
    } finally {
      setLoading(false);
    }
  };

  // Récupérer l'historique d'audit d'un exercice
  const getAuditHistory = async (fiscalYearId: string) => {
    setLoading(true);
    try {
      const response = await auditApi.getAuditHistory(fiscalYearId);
      if (response.success && response.data) {
        setAuditHistory(response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting audit history:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Récupérer la liste de tous les audits
  const getAuditList = async (filters?: AuditListFilters) => {
    setLoading(true);
    try {
      const response = await auditApi.getAuditList(filters);
      if (response.success && response.data) {
        setAuditList(response.data.data);
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting audit list:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    auditHistory,
    auditList,
    requestAuditToken,
    validateAuditToken,
    validateAudit, // Pour compatibilité avec le code existant
    auditFiscalYear,
    getAuditHistory,
    getAuditList
  };
}